import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

const C = {
  bg: '#0f0f11', panel: '#16161a', border: '#2a2a35', text: '#f0f0f2',
  muted: '#7a7a8a', amber: '#f5a623', green: '#3ecf8e', red: '#e8484a', blue: '#4f8ef7', purple: '#a78bfa'
};
const inp = { width: '100%', padding: '9px 12px', background: '#1a1a22', border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 14, boxSizing: 'border-box', outline: 'none' };

export default function TreatmentNotes() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    service: '',
    therapist: '',
    pressure: 'medium',
    areas_of_focus: '',
    areas_to_avoid: '',
    products_used: '',
    client_feedback: '',
    next_session_notes: '',
    private_notes: '',
    health_flags: '',
  });

  useEffect(() => {
    axios.get('/api/customers?limit=500').then(r => setCustomers(r.data.customers || r.data || [])).catch(() => {});
  }, []);

  async function loadNotes(customerId) {
    setLoading(true);
    try {
      const r = await axios.get('/api/treatment-notes?contact_id=' + customerId);
      setNotes(r.data.notes || []);
    } catch(e) { setNotes([]); }
    setLoading(false);
  }

  function selectCustomer(c) {
    setSelected(c);
    loadNotes(c.id);
  }

  async function saveNote() {
    if (!selected || !form.service) return alert('Select a customer and enter service');
    try {
      await axios.post('/api/treatment-notes', { ...form, contact_id: selected.id });
      setShowModal(false);
      resetForm();
      loadNotes(selected.id);
    } catch(e) { alert('Error saving note'); }
  }

  function resetForm() {
    setForm({ date: new Date().toISOString().split('T')[0], service: '', therapist: '', pressure: 'medium', areas_of_focus: '', areas_to_avoid: '', products_used: '', client_feedback: '', next_session_notes: '', private_notes: '', health_flags: '' });
  }

  const filtered = customers.filter(c => {
    const name = ((c.first_name || '') + ' ' + (c.last_name || '')).toLowerCase();
    return !search || name.includes(search.toLowerCase()) || (c.phone || '').includes(search);
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: C.bg, color: C.text }}>
      {/* Left: Customer list */}
      <div style={{ width: 280, borderRight: '1px solid ' + C.border, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '16px', borderBottom: '1px solid ' + C.border }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>📋 Treatment Notes</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ ...inp, fontSize: 13, padding: '7px 10px' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.slice(0, 50).map(c => {
            const name = ((c.first_name || '') + ' ' + (c.last_name || '')).trim();
            return (
              <div key={c.id} onClick={() => selectCustomer(c)}
                style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #1a1a22', background: selected?.id === c.id ? 'rgba(245,166,35,0.08)' : 'transparent', borderLeft: selected?.id === c.id ? '3px solid ' + C.amber : '3px solid transparent' }}
                onMouseEnter={e => { if (selected?.id !== c.id) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={e => { if (selected?.id !== c.id) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{c.phone || c.email || 'No contact'}</div>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={{ padding: 20, color: C.muted, textAlign: 'center', fontSize: 13 }}>No clients found</div>}
        </div>
      </div>

      {/* Right: Notes */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 48 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.muted }}>Select a client to view their treatment history</div>
          </div>
        ) : (
          <>
            {/* Client header */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.panel }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{((selected.first_name || '') + ' ' + (selected.last_name || '')).trim()}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {[selected.phone, selected.email].filter(Boolean).join(' · ')}
                  {selected.total_visits ? ' · ' + selected.total_visits + ' visits' : ''}
                  {selected.notes ? ' · ⚠️ ' + selected.notes : ''}
                </div>
              </div>
              <button onClick={() => setShowModal(true)} style={{ padding: '9px 20px', background: C.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer' }}>+ New Note</button>
            </div>

            {/* Notes list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {loading ? <div style={{ color: C.muted, textAlign: 'center', padding: 40 }}>Loading...</div>
              : notes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>No treatment notes yet</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>Click "+ New Note" to add the first session note</div>
                </div>
              ) : notes.map(note => (
                <div key={note.id} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 14, padding: '20px 24px', marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{note.service}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                        {note.date} {note.therapist ? '· ' + note.therapist : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, background: 'rgba(167,139,250,0.1)', color: C.purple }}>
                      {note.pressure} pressure
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      ['🎯 Areas of Focus', note.areas_of_focus],
                      ['⚠️ Areas to Avoid', note.areas_to_avoid],
                      ['🧴 Products Used', note.products_used],
                      ['💬 Client Feedback', note.client_feedback],
                      ['📅 Next Session', note.next_session_notes],
                      ['🏥 Health Flags', note.health_flags],
                    ].filter(([_, v]) => v).map(([label, val]) => (
                      <div key={label} style={{ background: '#0f0f11', borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 13, color: C.text }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {note.private_notes && (
                    <div style={{ marginTop: 12, background: 'rgba(232,72,74,0.05)', border: '1px solid rgba(232,72,74,0.15)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.red, textTransform: 'uppercase', marginBottom: 4 }}>🔒 Private Notes</div>
                      <div style={{ fontSize: 13, color: C.text }}>{note.private_notes}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* New Note Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '92vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: C.panel, zIndex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>📋 New Treatment Note</div>
              <button onClick={() => { setShowModal(false); resetForm(); }} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Date *</div>
                  <input type="date" value={form.date} onChange={e => setForm(f=>({...f,date:e.target.value}))} style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Service *</div>
                  <input value={form.service} onChange={e => setForm(f=>({...f,service:e.target.value}))} placeholder="e.g. Deep Tissue Massage" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Therapist</div>
                  <input value={form.therapist} onChange={e => setForm(f=>({...f,therapist:e.target.value}))} placeholder="Staff name" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>Pressure</div>
                  <select value={form.pressure} onChange={e => setForm(f=>({...f,pressure:e.target.value}))} style={inp}>
                    <option value="light">Light</option>
                    <option value="medium">Medium</option>
                    <option value="firm">Firm</option>
                    <option value="deep">Deep Tissue</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>🎯 Areas of Focus</div>
                  <input value={form.areas_of_focus} onChange={e => setForm(f=>({...f,areas_of_focus:e.target.value}))} placeholder="e.g. Lower back, shoulders" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>⚠️ Areas to Avoid</div>
                  <input value={form.areas_to_avoid} onChange={e => setForm(f=>({...f,areas_to_avoid:e.target.value}))} placeholder="e.g. Left knee injury" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>🧴 Products Used</div>
                  <input value={form.products_used} onChange={e => setForm(f=>({...f,products_used:e.target.value}))} placeholder="e.g. Lavender oil, hot stones" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>💬 Client Feedback</div>
                  <input value={form.client_feedback} onChange={e => setForm(f=>({...f,client_feedback:e.target.value}))} placeholder="How they felt after" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>🏥 Health Flags</div>
                  <input value={form.health_flags} onChange={e => setForm(f=>({...f,health_flags:e.target.value}))} placeholder="e.g. Pregnant, high BP, allergies" style={inp}/>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>📅 Next Session Notes</div>
                  <input value={form.next_session_notes} onChange={e => setForm(f=>({...f,next_session_notes:e.target.value}))} placeholder="Suggestions for next visit" style={inp}/>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: 'uppercase', marginBottom: 6 }}>🔒 Private Notes (not visible to client)</div>
                  <textarea value={form.private_notes} onChange={e => setForm(f=>({...f,private_notes:e.target.value}))} placeholder="Internal staff notes only..." rows={3}
                    style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }}/>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowModal(false); resetForm(); }} style={{ flex:1, padding:12, background:'transparent', color:C.muted, border:'1px solid '+C.border, borderRadius:10, fontWeight:600, cursor:'pointer' }}>Cancel</button>
                <button onClick={saveNote} style={{ flex:2, padding:12, background:C.amber, color:'#000', border:'none', borderRadius:10, fontWeight:800, cursor:'pointer', fontSize:15 }}>Save Note</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
